// Photo/video helpers: classify files, and shrink photos on the phone before
// upload (a 12 MP camera JPEG is 4-8 MB; ~2500px at q0.85 is ~1 MB and plenty).
const EXT_MIME = {
  jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp', heic: 'image/heic', heif: 'image/heif',
  mp4: 'video/mp4', mov: 'video/quicktime', webm: 'video/webm', '3gp': 'video/3gpp',
};

// Decode an image to something drawImage() accepts. Older Safari (e.g. iPadOS 16) rejects
// createImageBitmap's `imageOrientation: 'from-image'`, so fall back step by step to a plain
// <img> decode, which every browser supports and which applies the photo's EXIF rotation.
export async function decodeImage(blob, applyOrientation = true) {
  if (typeof createImageBitmap === 'function') {
    const attempts = applyOrientation ? [{ imageOrientation: 'from-image' }, null] : [null];
    for (const opts of attempts) {
      try { return await (opts ? createImageBitmap(blob, opts) : createImageBitmap(blob)); } catch (_) { /* try the next way */ }
    }
  }
  const url = URL.createObjectURL(blob);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    const c = document.createElement('canvas');
    c.width = img.naturalWidth;
    c.height = img.naturalHeight;
    c.getContext('2d').drawImage(img, 0, 0);
    return c; // has width/height, drawable like an ImageBitmap
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function mimeOf(file) {
  if (file.type) return file.type.toLowerCase();
  const ext = file.name?.split('.').pop()?.toLowerCase();
  return EXT_MIME[ext] || '';
}

export const isVideo = (file) => mimeOf(file).startsWith('video/');
export const isImage = (file) => mimeOf(file).startsWith('image/');
export const isSupported = (file) => Object.values(EXT_MIME).includes(mimeOf(file));

export function prettySize(bytes) {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toFixed(bytes > 100 * 1024 * 1024 ? 0 : 1)} MB`;
}

const MAX_EDGE = 2560;

// Returns { blob, mime }. Falls back to the original on anything unexpected
// (e.g. a HEIC the browser can't decode) -- the server accepts HEIC as-is.
export async function prepare(file) {
  const mime = mimeOf(file);
  if (!mime.startsWith('image/')) return { blob: file, mime };
  try {
    const bmp = await decodeImage(file);
    const scale = Math.min(1, MAX_EDGE / Math.max(bmp.width, bmp.height));
    if (scale === 1 && file.size < 1.5 * 1024 * 1024 && mime === 'image/jpeg') { bmp.close?.(); return { blob: file, mime }; }
    const w = Math.round(bmp.width * scale);
    const h = Math.round(bmp.height * scale);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    canvas.getContext('2d').drawImage(bmp, 0, 0, w, h);
    bmp.close?.();
    const blob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', 0.85));
    if (!blob || (scale === 1 && blob.size >= file.size)) return { blob: file, mime };
    return { blob, mime: 'image/jpeg' };
  } catch (_) {
    return { blob: file, mime };
  }
}
